import type { Requirement, RequirementNode, RequirementType } from '@ispot/shared'
import { RequirementsTable } from '../RequirementsTable'
import { RequirementsStatusBar } from '../RequirementsStatusBar'
import { RequirementDetailPanel } from '../RequirementDetailPanel'
import { ViewToolbar } from '../filters/ViewToolbar'
import { TableTrailing } from '../filters/TableTrailing'
import type { GroupBy } from '../CommandBar'
import type { ViewContainerProps } from './sharedProps'

interface TableViewProps extends ViewContainerProps {
  /** Tree pre-flattened/grouped for the spreadsheet view. */
  displayTree: RequirementNode[]
  visibleColumns: Record<string, boolean>
  updateColumnSettings: (cols: Record<string, boolean>) => void
  expandedIds: Set<string>
  selectedIds: Set<string>
  toggleExpanded: (id: string) => void
  toggleSelected: (id: string) => void
  deselectAll: () => void
  filterQuery: string
  activeFilters: {
    type?: string
    priority?: string
    status?: string
    themeIds?: string[]
    personaIds?: string[]
  }
  groupBy: GroupBy
  setGroupBy: (g: GroupBy) => void
  /** Add a child requirement under a parent. */
  onAddChild: (parentId: string) => void
  /** Direct create — used by inline row insertion. */
  createRequirement: (data: {
    name: string; description?: string; type: RequirementType; parentId?: string;
    priority?: Requirement['priority']; status?: Requirement['status'];
  }) => Promise<Requirement>
  generateChildren: (parentId: string, apiKey: string) => Promise<unknown>
  reorderRequirements: (parentId: string | null, orderedIds: string[]) => Promise<void>
  apiKey: string

  /** AI generate from research. */
  onGenerate: () => Promise<void>
  isGenerating: boolean
  newlyDetected: number

  /** Bulk actions and summary footer. The hook reads selectedIds internally. */
  summary: { total: number; byStatus: Record<string, number> } | null
  bulkUpdateStatus: (status: Requirement['status']) => Promise<void>
  bulkUpdatePriority: (priority: Requirement['priority']) => Promise<void>
  bulkDelete: () => Promise<void>
}

/**
 * Table (spreadsheet) view. Layout:
 *   no left rail. Toolbar (search / filter / chips / group / sort / columns / generate) → grid → status bar.
 */
export function TableView({
  accountId, projectId,
  requirements, themes,
  detailNode, detailId, setDetailId,
  updateRequirement, deleteRequirement,
  resolvePersona, errorBanner,

  displayTree,
  visibleColumns, updateColumnSettings,
  expandedIds, selectedIds, toggleExpanded, toggleSelected, deselectAll,
  filterQuery, activeFilters,
  groupBy, setGroupBy,
  onAddChild, createRequirement, generateChildren, reorderRequirements, apiKey,
  onGenerate, isGenerating, newlyDetected,
  summary, bulkUpdateStatus, bulkUpdatePriority, bulkDelete,
}: TableViewProps) {
  // Memoize themeById locally — small enough to avoid passing through props.
  const themeById = new Map(themes.map(t => [t.id, t]))

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <ViewToolbar
        requirements={requirements}
        themes={themes}
        hideGroup
        trailing={
          <TableTrailing
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            visibleColumns={visibleColumns}
            onVisibleColumnsChange={updateColumnSettings}
            onGenerate={onGenerate}
            isGenerating={isGenerating}
            newlyDetected={newlyDetected || undefined}
          />
        }
      />

      {errorBanner}

      <div className="flex flex-1 min-h-0" style={{ gap: 0 }}>
        <div
          className="flex-1 min-w-0 overflow-auto"
          style={{
            background: 'var(--color-bg-card)',
            padding: '0',
          }}
        >
          <RequirementsTable
            tree={displayTree}
            themeById={themeById}
            visibleColumns={visibleColumns}
            expandedIds={expandedIds}
            selectedIds={selectedIds}
            filterQuery={filterQuery}
            activeFilters={activeFilters}
            onToggleExpand={toggleExpanded}
            onToggleSelect={toggleSelected}
            onUpdate={updateRequirement}
            onDelete={deleteRequirement}
            onAddChild={onAddChild}
            onCreate={createRequirement}
            onGenerateChildren={(parentId: string) => generateChildren(parentId, apiKey)}
            onReorder={reorderRequirements}
            detailId={detailId}
            onToggleDetail={(id) => setDetailId(detailId === id ? null : id)}
            density="comfortable"
            resolvePersona={resolvePersona}
          />
        </div>

        {detailNode && (
          <RequirementDetailPanel
            accountId={accountId}
            projectId={projectId}
            node={detailNode}
            onUpdate={updateRequirement}
            onClose={() => setDetailId(null)}
            resolvePersona={resolvePersona}
          />
        )}
      </div>

      <RequirementsStatusBar
        summary={summary}
        selectedCount={selectedIds.size}
        onDeselectAll={deselectAll}
        onBulkStatus={bulkUpdateStatus}
        onBulkPriority={bulkUpdatePriority}
        onBulkDelete={bulkDelete}
      />
    </div>
  )
}
